import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { ClassGroupSummaryItem } from '../../../../constants/types/lms/assignment/type';
import TextWinds from '../../../common/TextWinds';
import { useMemo, useState } from 'react';
import { Input } from 'antd';

interface ClassListContentsProps {
  classList: ClassGroupSummaryItem[];
  handleClassClick: (classItem: ClassGroupSummaryItem) => void;
}

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 반 목록
 */
const ClassListContents = ({
  classList,
  handleClassClick,
}: ClassListContentsProps) => {
  const [classNameFilter, setClassNameFilter] = useState('');

  const filteredClassList = useMemo(() => {
    const keyword = classNameFilter.trim().toLowerCase();

    if (!keyword) return classList;

    return classList.filter((classItem) =>
      classItem.classGroupName.toLowerCase().includes(keyword),
    );
  }, [classList, classNameFilter]);

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <TextWinds type="title_h3">확인할 반을 선택해주세요.</TextWinds>

          <TextWinds type="caption1" color="gray8" className="mt-1 block">
            반을 선택하면 해당 반의 과제 진행 현황을 확인할 수 있습니다.
          </TextWinds>
        </div>

        <Input
          type="search"
          allowClear
          className="w-full xl:w-[240px]"
          placeholder="반명 검색"
          value={classNameFilter}
          onChange={(event) => setClassNameFilter(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-3">
        {filteredClassList.length === 0 ? (
          <div className="flex h-[240px] items-center justify-center rounded-xl border border-gray4 bg-gray0 px-4 text-center">
            <TextWinds type="content_body2" color="gray6">
              조회된 반 목록이 없습니다.
            </TextWinds>
          </div>
        ) : (
          <>
            {filteredClassList.map((classItem) => {
              const progressColorClassName =
                classItem.completionRate <= 0
                  ? 'bg-gray3'
                  : classItem.completionRate >= 100
                    ? 'bg-green-500'
                    : 'bg-[#0B8ED0]';

              return (
                <button
                  key={classItem.classGroupId}
                  type="button"
                  className="group flex w-full items-center gap-4 rounded-xl border border-gray3 bg-white px-4 py-4 text-left transition-all duration-200 hover:border-purple5 hover:bg-purple1 hover:shadow-sm active:scale-[0.99]"
                  onClick={() => handleClassClick(classItem)}
                >
                  <div className="min-w-0 flex-1">
                    <TextWinds
                      type="content_body2"
                      color="gray9"
                      className="truncate font-bold group-hover:text-purple5"
                    >
                      {classItem.classGroupName}
                    </TextWinds>

                    <TextWinds
                      type="caption1"
                      color="gray8"
                      className="mt-0.5 block"
                    >
                      학생 {classItem.studentCount ?? 0}명 · 과제{' '}
                      {classItem.todoCount ?? 0}개
                    </TextWinds>

                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray1">
                        <div
                          className={[
                            'h-full rounded-full transition-all duration-300',
                            progressColorClassName,
                          ].join(' ')}
                          style={{ width: `${classItem.completionRate}%` }}
                        />
                      </div>

                      <TextWinds
                        type="caption1"
                        color="gray8"
                        className="hidden w-[96px] shrink-0 text-right font-bold xl:block"
                      >
                        전체 완료율 {classItem.completionRate}%
                      </TextWinds>
                    </div>
                  </div>

                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray6 group-hover:text-purple5" />
                </button>
              );
            })}
          </>
        )}
      </div>
    </>
  );
};

export default ClassListContents;
